import React, {FunctionComponent, useCallback, useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {Label} from "reactstrap";
import usePaginatedData from "../../../hooks/usePaginatedData";
import {OrderSearchObject} from "../../../interfaces/OrderInterfaces";
import {customerService} from "../../../services/CustomerService";
import {ColorType} from "../../../types/bootstrap/BootstrapType";
import Button from "../../atoms/Button";
import DateFilter from "../filters/DateFilter";
import FilterGroup from "../filters/FilterGroup";
import Pagination from "../pagination/Pagination";
import OrderTable from "../table/order/OrderTable";
import {ResourceDisplayOption} from "../../../constants/ResourceDisplayOptionConstant";
import TextInput from "../../atoms/input/TextInput";

interface CustomerOrderListProps {
  className?: string,
  resourceDisplayOption: ResourceDisplayOption,
  searchObject: OrderSearchObject,
  updateSearchField: (field: string, value: string | string[]) => void,
  onReset: () => void
}

const CustomerOrderList: FunctionComponent<CustomerOrderListProps> = ({
  className = "",
  resourceDisplayOption,
  searchObject,
  updateSearchField,
  onReset,
}) => {
  const intl= useIntl()
  const [localSearchValue, setLocalSearchValue] = useState(searchObject?.customerOrCode || "");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalSearchValue(searchObject?.customerOrCode || "");
  }, [searchObject?.customerOrCode]);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (value.length >= 3 || value.length === 0) {
        updateSearchField("customerOrCode", value);
      }
    }, 500);
  }, [updateSearchField]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const {
    data,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortOptions,
    setSortOptions,
    total,
    totalElements,
  } = usePaginatedData((params, filter) => customerService.getCustomersOrderPage(params, filter), searchObject)

  return (
    <div className={`mb-5 ${className ?? ""}`}>
      <FilterGroup className="d-flex gap-2">
        <div className="epow-filter main-filter">
          <div className="epow-label">
            <Label>{intl.formatMessage({id: "order_filter_code_title"})}</Label>
          </div>
          <TextInput
            value={localSearchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={intl.formatMessage({id: "order_filter_code_placeholder"})}
          />
        </div>
        <DateFilter
          value={searchObject.startDate}
          onChangeDate={(value) => updateSearchField("startDate", value)}
          title={intl.formatMessage({id: "order_filter_period_start"})}
        />
        <DateFilter
          value={searchObject.endDate}
          onChangeDate={(value) => updateSearchField("endDate", value)}
          title={intl.formatMessage({id: "order_filter_period_end"})}
        />
        <div>
          <Button onClick={onReset} color={ColorType.SECONDARY} >{intl.formatMessage({id: "header_menu_clear_search"})}</Button>
        </div>
      </FilterGroup>

      <OrderTable resourceDisplayOption={resourceDisplayOption} orders={data} total={totalElements} sortOptions={sortOptions} onSortOptions={setSortOptions} isCustomer />
      <Pagination page={page} setPage={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} total={total} />
    </div>
  )
}

export default CustomerOrderList;
